/**
 * The sound controls: mute and the master, music and effects levels,
 * remembered across visits (GameAudio prefs). The settings panel shows the
 * whole set; the speaker button's popover (soundPopover.ts) the two levels
 * that matter mid-match.
 */

import { getAudio } from '../audio/audio.js';
import { el } from './dom.js';

type Level = 'master' | 'music' | 'sfx';

export interface SoundControls {
  nodes: HTMLElement[];
  /** Re-read the prefs into the widgets (M may have flipped the mute while they were out of sight). */
  sync(): void;
}

export function soundControls(opts: { master?: boolean; hint?: boolean } = {}): SoundControls {
  const audio = getAudio();
  const sliders: { key: Level; input: HTMLInputElement }[] = [];
  const slider = (label: string, key: Level): HTMLElement => {
    const input = el('input', {
      type: 'range', min: 0, max: 100, step: 1, value: Math.round(audio.prefs[key] * 100), 'aria-label': `${label} volume`,
      oninput: () => { audio.unlock(); audio.setPrefs({ [key]: Number(input.value) / 100 }); },
      // A pointer drag must not leave the slider focused, or the arrow keys would move it instead of the wizard.
      onpointerup: () => input.blur(),
    });
    sliders.push({ key, input });
    return el('label', { class: 'sound-row' }, el('span', null, label), input);
  };
  const mute = el('input', { type: 'checkbox', 'aria-label': 'Mute all sound' });
  mute.checked = audio.prefs.muted;
  mute.addEventListener('change', () => { audio.unlock(); audio.setMuted(mute.checked); });
  const nodes: HTMLElement[] = [el('label', { class: 'sound-row' }, el('span', null, 'Mute'), mute)];
  if (opts.master) nodes.push(slider('Master', 'master'));
  nodes.push(slider('Music', 'music'), slider('Effects', 'sfx'));
  if (opts.hint) nodes.push(el('span', { class: 'muted sound-hint' }, 'M mutes in a match; the speaker button by the gear has these levels too.'));
  return {
    nodes,
    sync: () => {
      mute.checked = audio.prefs.muted;
      for (const { key, input } of sliders) input.value = String(Math.round(audio.prefs[key] * 100));
    },
  };
}

/** The Sound section of the settings panel. */
export function soundSettings(): HTMLElement {
  const controls = soundControls({ master: true, hint: true });
  const node = el('div', { class: 'settings-sound' }, el('h3', null, 'Sound'), ...controls.nodes);
  // Keep the panel honest if M was pressed while it was open.
  node.addEventListener('pointerenter', () => controls.sync());
  return node;
}
